import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { DependencyInfoOptions, ProjectType } from '../utils';
import type { DependencyInfoOption } from '../../shared/types';
import { AccessGate, ButtonGroup, LabeledCheckbox } from '../../../../generic';
import styles from './SidePanel.module.less';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import Button from '../../../../generic/Button';
import Locator from '../../../../../../shared/Locator';
import { FILE_PATH_IN_REPORTS } from '../../../../../../shared/featureFlags/flagNames';
import { isFeatureEnabled } from '../../../../../helpers/features';
import { useForceReportReRender } from '../hooks';

type Props = {
  /**
   * Should show the modal
   */
  shouldShowModal: boolean;
  /**
   * On hide action
   */
  onHide(): void;
};

/**
 * Provides a modal for selecting which dependency metadata to include in the report.
 */
const DependencyInfoModal: VoidFunctionComponent<Props> = ({ shouldShowModal, onHide }) => {
  const dispatch = useDispatch();
  const forceReportReRender = useForceReportReRender();
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext);
  const projectType = reportContext.projectType;
  const optionList = useSelector(ReportGenerationStore.selectors.getDependencyInfoOptions);
  const [optionState, setOptionState] = useState<Set<DependencyInfoOption>>(new Set(optionList));

  // We need to reset the state when the optionList changes, otherwise we'll only have the default values
  // Because of how the data is mounted through the project container, this will only update once
  useEffect(() => setOptionState(new Set(optionList)), [optionList]);

  const depInfoOptions = Object.entries(DependencyInfoOptions)
    .filter(([option]) => {
      if (option === 'Project' && projectType !== ProjectType.ReleaseGroup) {
        return false;
      }

      if (option === 'FilePath' && !isFeatureEnabled(FILE_PATH_IN_REPORTS)) {
        return false;
      }

      return true;
    });

  const handleToggle = (option: DependencyInfoOption, value: boolean) => {
    if (!value) {
      setOptionState(prevState => {
        const nextState = new Set(prevState);
        nextState.delete(option);

        return nextState;
      });
      return;
    }

    setOptionState(prevState => new Set([...prevState, option]));
  };

  const handleSave = async () => {
    const selectedOptions = Array.from(optionState);
    dispatch(ReportGenerationStore.actions.setDependencyInfoOptions(selectedOptions));
    onHide();

    // For projects, also save to the backend
    if (projectType === ProjectType.Project && 'revision' in reportContext) {
      await dispatch(
        ReportGenerationStore.actions.updateProjectDependencyInfoOptions(
          Locator.parsePackageString(reportContext.revision), selectedOptions,
        ),
      );
    }

    await forceReportReRender();
  };

  return (
    <div>
      <Modal show={shouldShowModal} onHide={onHide}>
        <Modal.Header closeButton>
          Select dependency metadata to include
        </Modal.Header>
        <Modal.Body className={styles.reportDependencyInfoModal}>
          <AccessGate>
            <div className={styles.reportDependencyInfoModalOptions}>
              {depInfoOptions.map(([key, label]) =>
                <div>
                  <LabeledCheckbox
                    key={key}
                    label={label}
                    isChecked={optionState.has(key) || false}
                    onChange={() => handleToggle(key, !optionState.has(key))}
                  />
                </div>)}
            </div>
          </AccessGate>
        </Modal.Body>
        <Modal.Footer>
          <AccessGate behavior="hide">
            <ButtonGroup spacing="xs">
              <Button
                onClick={onHide}
                type="secondary"
                size="medium"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                type="primary"
                size="medium"
                >
                Confirm
              </Button>
            </ButtonGroup>
          </AccessGate>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DependencyInfoModal;
